import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Footer from '../components/Footer';
import { Link as RouterLink } from 'react-router-dom';

const FAQPage = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // FAQ categories
  const categories = [
    {
      title: 'General Questions',
      faqs: [
        {
          question: 'What is QResto?',
          answer:
            'QResto is an augmented reality (AR) menu solution for restaurants. We transform your menu into an interactive AR experience accessible through a simple QR code scan, without requiring any app downloads.',
        },
        {
          question: 'How does the QR code AR menu work?',
          answer:
            'Customers scan your custom QR code using their smartphone camera, which opens a web page with our AR technology. When they point their phone at the QR code, your menu content appears in augmented reality, allowing them to browse pages, view images, and interact with your menu.',
        },
        {
          question: 'Do customers need to download an app?',
          answer:
            'No, that\'s one of the biggest advantages of QResto. Our solution works directly in web browsers on modern smartphones. No app downloads are required, creating a frictionless experience for your customers.',
        },
        {
          question: 'Does it work on all smartphones?',
          answer:
            'QResto works on most modern smartphones (iOS and Android) with the latest browsers that support WebAR technology. For devices that don\'t support AR, we\'re developing a fallback option that will display a standard digital menu.',
        },
      ],
    },
    {
      title: 'Pricing & Plans',
      faqs: [
        {
          question: 'How much does QResto cost?',
          answer:
            'QResto offers three main pricing tiers: Basic (₹12,499), Professional (₹24,999), and Premium (₹39,999). Each is a one-time setup fee with no monthly subscriptions. The plans differ in the number of QR codes, menu pages, and additional features included.',
        },
        {
          question: 'Are there any hidden fees or monthly charges?',
          answer:
            'No, we pride ourselves on transparent pricing. The costs mentioned are one-time setup fees with no recurring monthly charges. Menu updates and maintenance packages are available at additional costs if needed.',
        },
        {
          question: 'What\'s included in each pricing tier?',
          answer:
            'Basic tier includes a single QR code and up to 5 menu pages. Professional tier includes up to 3 QR codes, 15 menu pages, and custom branding. Premium tier offers unlimited QR codes and menu pages, along with 3D dish models and analytics. Visit our Pricing page for a detailed comparison.',
        },
        {
          question: 'Do you offer custom solutions for larger restaurant chains?',
          answer:
            'Yes, we offer custom enterprise solutions for restaurant chains with multiple locations. Please contact us directly to discuss your specific requirements and for custom pricing.',
        },
      ],
    },
    {
      title: 'Technical Questions',
      faqs: [
        {
          question: 'What happens if a customer\'s device doesn\'t support AR?',
          answer:
            'We\'re currently developing a fallback solution that will provide a standard digital menu experience for devices that don\'t support AR. This ensures all your customers can access your menu, regardless of their device capabilities.',
        },
        {
          question: 'Can I update my menu after it\'s created?',
          answer:
            'Yes, menu updates are available at an additional fee. We offer menu maintenance packages that allow for regular updates to your menu items, prices, and images. Contact us for specific pricing on menu maintenance options.',
        },
        {
          question: 'What file formats do you accept for menu images?',
          answer:
            'We accept high-resolution images in JPG, PNG, and WEBP formats. For the best AR experience, we recommend professionally shot food photography with good lighting and clear backgrounds.',
        },
        {
          question: 'Is internet connectivity required for the AR menu to work?',
          answer:
            'Yes, customers need an internet connection to access the AR menu. The initial load is optimized to work efficiently on standard mobile data connections.',
        },
      ],
    },
    {
      title: 'Implementation & Support',
      faqs: [
        {
          question: 'How long does the setup process take?',
          answer:
            'The typical setup process takes 3-5 business days from the time you provide all your menu details and images. For more complex menus or custom requirements, it may take longer.',
        },
        {
          question: 'What information do I need to provide to get started?',
          answer:
            'You\'ll need to provide your menu items with descriptions, prices, and high-quality images of your dishes. Additionally, any specific branding elements like your logo, color scheme, and restaurant information will help us create a customized experience.',
        },
        {
          question: 'What kind of support do you offer?',
          answer:
            'All plans include setup support to get your AR menu running. Basic plans include standard email support. Professional plans include priority email support with faster response times. Premium plans include dedicated support with a designated account manager.',
        },
        {
          question: 'Do you offer training for our staff?',
          answer:
            'Yes, we provide a comprehensive onboarding guide and training materials for your staff. For Premium plan customers, we also offer a virtual training session to ensure your team is comfortable with the QR AR menu system.',
        },
      ],
    },
  ];

  return (
    <>
      {/* Header Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: { xs: 10, md: 12 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}
          >
            Get answers to common questions about QResto's AR menu solutions
          </Typography>
        </Container>
      </Box>

      {/* FAQ Content Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {categories.map((category, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ mb: 4, fontWeight: 600 }}
                >
                  {category.title}
                </Typography>

                {category.faqs.map((faq, faqIndex) => (
                  <Accordion
                    key={faqIndex}
                    expanded={expanded === `${index}-${faqIndex}`}
                    onChange={handleChange(`${index}-${faqIndex}`)}
                    sx={{
                      mb: 2,
                      boxShadow: 'none',
                      '&:before': {
                        display: 'none',
                      },
                      backgroundColor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-${faqIndex}-content`}
                      id={`panel${index}-${faqIndex}-header`}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          my: 2,
                        },
                      }}
                    >
                      <Typography variant="h6" fontWeight="medium">
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ pb: 1 }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Still Have Questions Section */}
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Divider sx={{ mb: 6 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Still Have Questions?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}
          >
            We're here to help! Contact our team directly for personalized assistance with any questions about QResto's AR menu solutions.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/contact"
            sx={{ py: 1.5, px: 4 }}
          >
            Contact Us
          </Button>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default FAQPage;